
import React from 'react';
import { 
  LayoutDashboard, 
  BrainCircuit, 
  Workflow, 
  Calculator, 
  PieChart, 
  Users, 
  LogOut, 
  X,
  Cable,
  Scale,
  ShieldCheck,
  FileText,
  HardDrive,
  Brain,
  Sparkles,
  Network,
  Activity
} from 'lucide-react';
import { AppView, Lead } from '../types';

interface SidebarProps {
  currentView: AppView;
  onNavigate: (view: AppView) => void;
  onTogglePublic: () => void;
  onCloseMobile: () => void;
  leads: Lead[];
}

const Sidebar: React.FC<SidebarProps> = ({ currentView, onNavigate, onTogglePublic, onCloseMobile }) => {
  
  const NavItem = ({ view, icon: Icon, label }: { view: AppView; icon: any; label: string }) => {
    const isActive = currentView === view;
    return (
      <button
        onClick={() => onNavigate(view)}
        className={`w-full flex items-center px-4 py-3 mb-1 rounded-xl transition-all duration-200 group ${
          isActive 
            ? 'bg-blue-600 text-white shadow-[0_0_20px_rgba(37,99,235,0.3)] border border-blue-500/50' 
            : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-100 hover:translate-x-1'
        }`}
      >
        <Icon className={`w-5 h-5 mr-3 ${isActive ? 'text-white' : 'text-slate-500 group-hover:text-blue-400'}`} />
        <span className="font-medium text-sm">{label}</span>
      </button>
    );
  };

  return (
    <div className="h-full w-72 bg-slate-950 border-r border-slate-800/60 flex flex-col shadow-2xl relative">
       {/* Close Button Mobile */}
       <button onClick={onCloseMobile} className="md:hidden absolute top-4 right-4 text-slate-400">
         <X className="w-6 h-6" />
       </button>

      <div className="p-6 border-b border-slate-800/60 flex items-center">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg mr-3 border border-slate-700 overflow-hidden bg-slate-900/80 flex-shrink-0 relative">
            <img src="/arkorbit-wappen.png" alt="ARKORBIT" className="absolute inset-0 w-full h-full object-cover object-center" style={{ transform: 'scale(1.9) translateY(23%)' }} />
        </div>
        <div>
            <h1 className="text-xl font-bold text-white tracking-tight">ARKORBIT</h1>
            <p className="text-xs text-blue-400 font-bold tracking-widest uppercase">AI AUSTRIA</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-8">
        
        <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 px-4">CEO · Donna</h3>
            <NavItem view={AppView.DONNA_WORKSPACE} icon={Sparkles} label="Donna Workspace" />
            <NavItem view={AppView.GRAPHIFY_DEV_MAP} icon={Network} label="Dev-Map (Graphify)" />
            <button
                onClick={() => window.open('/health-map/index.html', '_blank')}
                className="w-full flex items-center px-4 py-3 mb-1 rounded-xl transition-all duration-200 text-slate-400 hover:bg-slate-800/50 hover:text-white hover:translate-x-1 group"
            >
                <Activity className="w-5 h-5 mr-3 text-cyan-500 group-hover:text-cyan-400" />
                <span className="font-medium text-sm">Ops Health Map</span>
            </button>
        </div>

        <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 px-4">Kanzlei Infrastruktur</h3>
            <NavItem view={AppView.DASHBOARD} icon={LayoutDashboard} label="Command Center" />
            <NavItem view={AppView.BOT_DEMO} icon={FileText} label="ARKORBIT Secure Bot" />
            <NavItem view={AppView.BRAIN_UPLOAD} icon={Brain} label="ARKORBIT BRAIN" />
            <button
                onClick={() => window.open('https://cloud.arkorbit.at', '_blank')}
                className="w-full flex items-center px-4 py-3 mb-1 rounded-xl transition-all duration-200 text-slate-400 hover:bg-slate-800/50 hover:text-white hover:translate-x-1 group"
            >
                <HardDrive className="w-5 h-5 mr-3 text-emerald-500 group-hover:text-emerald-400" />
                <span className="font-medium text-sm">Datentresor (Cloud)</span>
            </button>
            <NavItem view={AppView.WORKFLOW_MONITOR} icon={Workflow} label="Frankfurt Node" />
            <NavItem view={AppView.INTEGRATIONS} icon={Cable} label="Schnittstellen (BMD/RZL)" />
        </div>

        <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 px-4">Analyse & Sales</h3>
            <NavItem view={AppView.DEEP_AUDIT} icon={BrainCircuit} label="Prozess Audit AI" />
            <NavItem view={AppView.ROI_CALCULATOR} icon={Calculator} label="Kosten-Analyse" />
            <NavItem view={AppView.PIPELINE} icon={PieChart} label="Mandanten Pipeline" />
        </div>

        <div>
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 px-4">Partnerschaft</h3>
            <NavItem view={AppView.AFFILIATE_PORTAL} icon={Users} label="Partner Portal" />
        </div>

      </div>

      <div className="p-4 border-t border-slate-800/60 bg-slate-900/30">
        <div className="flex items-center justify-center mb-4 text-[10px] text-emerald-500 uppercase font-bold tracking-widest">
            <ShieldCheck className="w-3 h-3 mr-1" /> 100% DSGVO Konform
        </div>
        <button 
            onClick={onTogglePublic}
            className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-slate-400 hover:text-white transition-colors"
        >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
